import { useEffect, useRef, useState } from 'react'
import { IconAlertCircle, IconLoader } from '@tabler/icons'
import { FileTypes } from "./Gallery"
import { copyStringToClipboard } from '../../../utils'
import CircularProgressBar from '../../CircularProgressBar'
import { Dropdown } from '../../Dropdown'
import { UtilityModal } from '../../Modal'
import ImageCarousel from './ImageCarousel';
import PdfThumbnail from './PdfThumbnail';
import { formatAssetDate, formatAssetFileSize, isPdfAsset } from '../utils'

interface ImageCardProps {
  item: FileTypes
  selectedFiles: Array<number>
  setSelectedFiles: any
  done: (file: { url: string, uid?: string, fileName?: string }) => void
  uid?: string
  handleClose: () => void
  index: number
  files: Array<FileTypes>
  imgOption: Array<{ value: string, icon: any, options?: Array<Object>, callback?: any }>
  moveOptions: Array<{ value: string, label: string }>
  progress: any,
  deleteConfirmation: any
  error: any
  standalone?: boolean
}

const ImageCard = ({
  item,
  selectedFiles,
  setSelectedFiles,
  done,
  uid,
  handleClose,
  index,
  files,
  imgOption,
  moveOptions,
  progress,
  deleteConfirmation,
  error,
  standalone = false,
}:ImageCardProps) => {
  const [copySuccess, setCopySuccess] = useState(false)
  const copyHideRef = useRef<number | null>(null)
  const isPdf = isPdfAsset(item)
  const fileSizeLabel = formatAssetFileSize(item.asset_file_size)
  const uploadedDate = formatAssetDate(item.created_at)

  useEffect(() => {
    return () => {
      if (copyHideRef.current !== null) {
        clearTimeout(copyHideRef.current)
        copyHideRef.current = null
      }
    }
  }, [])

  const copyableAssetUrl = () => {
    let url = isPdf ? item?.url : (item?.large_url || item?.url)
    if (url?.startsWith('//')) {
      url = `${window.location.protocol}${url}`
    }
    return url
  }

  const assetUrl = () =>
    isPdf ? item?.url : (item?.large_url || item?.url)

  const handleCardAction = () => {
    if (selectedFiles.length) {
      if (selectedFiles.includes(Number(item?.id))) {
        setSelectedFiles((prev: Array<number>) => prev.filter((id: number) => id !== Number(item?.id)))
      } else {
        setSelectedFiles((prev: Array<number>) => [...prev, Number(item?.id)])
      }
    } else if (standalone) {
      openAssetPreview()
    } else {
      done({
        url: assetUrl(),
        ...(uid && {
          uid,
          fileName: item?.asset_file_name
        })
      })
      handleClose()
    }
  }

  const handleCopyUrl = () => {
    const text = copyableAssetUrl()
    if (!text) return
    copyStringToClipboard(text)
    if (copyHideRef.current !== null) {
      clearTimeout(copyHideRef.current)
    }
    setCopySuccess(true)
    copyHideRef.current = window.setTimeout(() => {
      copyHideRef.current = null
      setCopySuccess(false)
    }, 1000)
  }

  const getPreviewImages = () =>
    files.map((file) => ({
      original: file.url,
      thumbnail: file.thumb_square_url || file.url,
      isPdf: isPdfAsset(file),
      fileName: file.asset_file_name,
      fileSize: formatAssetFileSize(file.asset_file_size) ?? undefined,
    }))

  const openAssetPreview = () => {
    UtilityModal({
      id: 'imagePreview',
      height: '100%',
      width: '100%',
      style: { background: 'black' },
      body: (props) => (
        <ImageCarousel
          images={getPreviewImages()}
          handleClose={props.handleClose}
          selectedImage={index}
        />
      ),
    })
  }

  const cardFilter = selectedFiles.includes(Number(item?.id)) ? 'brightness(0.75)' : 'brightness(1)'
  const pdfMeta = [fileSizeLabel, uploadedDate].filter(Boolean).join(' · ')

  return (
    <div className='relative group cursor-pointer h-[120px] bg-[#EFF0F7] rounded-lg'>
      {isPdf ? (
        <div
          className="h-[120px] w-full"
          onClick={handleCardAction}
          style={{ filter: cardFilter, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.04)' }}
        >
          <PdfThumbnail url={item.url} />
        </div>
      ) : (
        <img
          src={item?.thumb_square_url || item?.url}
          alt={item?.name}
          className="w-full h-[120px] rounded-lg object-cover"
          onClick={handleCardAction}
          style={{ filter: cardFilter, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.04)' }}
          loading="lazy"
        />
      )}
      {isPdf && (
        <span className="pointer-events-none absolute top-2 left-8 z-[1] rounded bg-danger px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none text-white">
          PDF
        </span>
      )}
      {/* Card Right Options */}
      <div
        className={`hidden items-center justify-center gap-1 rounded px-1 py-0 w-auto h-[20px] bg-[#e6eeffb3] absolute top-2 right-2 z-10 ${selectedFiles.length ? 'hidden' : 'group-hover:flex'} `}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {
          imgOption.map(({ value, icon, options, callback }: { value: string, icon: any, options?: Array<Object>, callback?: any }) => {
            if (value === 'view') {
              return (
                <div
                  key={value}
                  onClick={(e) => {
                    e.stopPropagation()
                    openAssetPreview()
                  }}
                  className="relative flex h-[20px] min-h-[20px] w-[20px] shrink-0 items-center justify-center p-0 leading-none"
                >
                  {icon}
                </div>
              )
            }
            if (value === 'copyUrl') {
              if (item?.name === 'Uploading' || !item?.id) return null
              return (
                <button
                  key={value}
                  type="button"
                  title="Copy URL"
                  aria-label="Copy URL"
                  onClick={() => {
                    handleCopyUrl()
                  }}
                  className="relative flex h-[20px] min-h-[20px] w-[20px] shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 leading-none text-inherit focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                >
                  {icon}
                </button>
              )
            }
            const dropdownProps = {
              component: icon,
              value,
              options: value === 'move' ? moveOptions : deleteConfirmation(item),
              width: '170px',
              callback: (e: any) => callback(e, item.id),
              autoAdjustBottom: value === 'move',
              maxHeight: value === 'move' ? 'max-h-[180px]' : 'max-h-[80px]',
              offsetHeight: value === 'move' ? 400 : 0,
              alignLeft: (index + 1) % 4 === 0,
              noWidth: true,
            }

            return (
                <span key={value} className="flex h-[20px] min-h-[20px] shrink-0 items-center justify-center p-0 leading-none [&_button]:m-0 [&_button]:flex [&_button]:h-[20px] [&_button]:min-h-[20px] [&_button]:w-[20px] [&_button]:items-center [&_button]:justify-center [&_button]:border-0 [&_button]:bg-transparent [&_button]:p-0 [&_button]:leading-none">
                  <Dropdown
                    {...dropdownProps}
                    action="primary"
                    scrollHint={value === 'move'}
                    portaled={value === 'move'}
                    portalZIndex={standalone ? 9999 : 1000001}
                  />
                </span>
              )
          })
        }
      </div>
      {copySuccess && (
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 rounded bg-secondary px-2 py-1 text-xs text-white shadow"
          role="status"
        >
          URL copied
        </div>
      )}
      {/* File name & metadata */}
      {isPdf ? (
        <div
          className={`absolute bottom-0 flex w-full flex-col rounded-b-lg p-2 text-white media-asset-background ${selectedFiles.length ? 'hidden' : ''}`}
          onClick={handleCardAction}
        >
          <span className="truncate text-xs font-medium">{item.asset_file_name}</span>
          {pdfMeta && <span className="truncate text-[10px] text-white/80">{pdfMeta}</span>}
        </div>
      ) : (
        <div
          className={`hidden justify-between rounded items-center text-xs p-3 w-full text-white absolute bottom-0 media-asset-background ${selectedFiles.length ? 'hidden' : 'group-hover:flex'} `}
          onClick={handleCardAction}
        >
          <span className="truncate">{item.asset_file_name}</span>
        </div>
      )}
      {/* Left Option - checkbox */}
      <div className={`absolute top-[6px] left-2 w-[20px] h-[20px] group-hover:flex justify-center items-center ${selectedFiles.length ? 'flex' : 'hidden'}`}>
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            className="form-checkbox h-4 w-4 bg-transparent"
            checked={selectedFiles.includes(item.id || -1)}
            onChange={(e) => {
              e.stopPropagation()
              if (e.target.checked) {
                setSelectedFiles([...selectedFiles, item.id || -1])
              } else {
                setSelectedFiles(selectedFiles.filter((id) => id !== item.id))
              }
            }}
          />
          <span className="ml-2 font-light"></span>
        </label>
      </div>
      {
        item?.name === 'Uploading' &&
        <div className='absolute top-0 left-0 w-full h-full flex justify-center items-center' style={{ background: 'rgba(239, 240, 247, 0.9)' }}>
          <div className='flex flex-col justify-center items-center max-w-[300px] text-center'>
            {
              error ?
                <IconAlertCircle size="48px" stroke={1.5} className='text-danger' />
                :
                !progress?.[item?.asset_file_name] || progress?.[item?.asset_file_name] == 100 ?
                  <IconLoader size="48px" stroke={1.5} className='text-primary' />
                  :
                  <CircularProgressBar progress={progress?.[item?.asset_file_name]} stroke={5} radius={35} />
            }
          </div>
        </div>
      }
    </div>
  )
}

export default ImageCard